// src/pages/auth/ForgotPassword.jsx
import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useSettings } from "../../context/SettingsContext";
import { useThemeContext } from "../../context/ThemeContext";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
  Link,
  Stepper,
  Step,
  StepLabel,
  Stack,
  useMediaQuery,
  Fade,
  Slide,
} from "@mui/material";
import {
  Email,
  Lock,
  VpnKey,
  ArrowBack,
  Security,
  CheckCircle,
} from "@mui/icons-material";
import axiosInstance from "../../api/axiosConfig";

const steps = ["Enter Email", "Verify OTP", "Reset Password"];

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { siteName, siteLogo } = useSettings();
  const { colors } = useThemeContext();

  const isMobile = useMediaQuery("(max-width:600px)");
  const safeSiteName = siteName || "V-Tech"; // ✅ SAFE FALLBACK

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const primaryColor = colors?.primaryColor || "#E31E24";
  const primaryDark = colors?.primaryColor
    ? darkenColor(colors.primaryColor, 20)
    : "#B71C1C";
  const isDark = colors?.themeMode === "dark";

  function darkenColor(hex, percent) {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max((num >> 16) - amt, 0);
    const G = Math.max(((num >> 8) & 0x00ff) - amt, 0);
    const B = Math.max((num & 0x0000ff) - amt, 0);
    return `#${((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1)}`;
  }

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address");
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await axiosInstance.post("/auth/forgot-password", {
        email,
      });
      setSuccess(response.data.message);
      setActiveStep(1);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send reset code");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await axiosInstance.post("/auth/verify-otp", {
        email,
        otp,
      });
      setSuccess(response.data.message);
      setActiveStep(2);
    } catch (err) {
      setError(err.response?.data?.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await axiosInstance.post("/auth/reset-password", {
        email,
        otp,
        newPassword,
      });
      setSuccess(response.data.message);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setError(null);
    setSuccess(null);
    if (activeStep === 0) navigate("/login");
    else setActiveStep((prev) => prev - 1);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isDark
          ? "linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 50%, #16213e 100%)"
          : `linear-gradient(135deg, ${primaryColor}08 0%, #f0f0f5 50%, #ffffff 100%)`,
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -150,
          right: -150,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${primaryColor}15 0%, transparent 70%)`,
          animation: "float 20s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": { transform: "translate(0, 0) scale(1)" },
            "50%": { transform: "translate(-40px, -20px) scale(1.1)" },
          },
        }}
      />
      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Slide direction="up" in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              width: "100%",
              borderRadius: { xs: 3, sm: 4 },
              bgcolor: isDark
                ? "rgba(30, 30, 46, 0.92)"
                : "rgba(255, 255, 255, 0.88)",
              backdropFilter: "blur(20px)",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.4)"}`,
              boxShadow: isDark
                ? "0 30px 80px rgba(0,0,0,0.5)"
                : "0 30px 80px rgba(0,0,0,0.08)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${primaryColor}, ${primaryDark}, ${primaryColor})`,
                backgroundSize: "200% 100%",
                animation: "gradientMove 3s ease-in-out infinite",
                "@keyframes gradientMove": {
                  "0%": { backgroundPosition: "0% 50%" },
                  "50%": { backgroundPosition: "100% 50%" },
                  "100%": { backgroundPosition: "0% 50%" },
                },
              }}
            />

            <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4 } }}>
              <Box sx={{ mb: 2 }}>
                {siteLogo ? (
                  <Box
                    sx={{
                      width: isMobile ? 80 : 120,
                      height: isMobile ? 70 : 100,
                      mx: "auto",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {/* ✅ FIX: Use siteLogo DIRECTLY (Context already has full URL) */}
                    <img
                      src={siteLogo}
                      alt={safeSiteName}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: isMobile ? 60 : 70,
                      height: isMobile ? 60 : 70,
                      mx: "auto",
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
                      borderRadius: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 12px 40px ${primaryColor}4D`,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      sx={{ color: "white", fontWeight: 900 }}
                    >
                      {/* ✅ FIX: Use safeSiteName */}
                      {safeSiteName.charAt(0)}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Typography
                variant={isMobile ? "h5" : "h4"}
                fontWeight="800"
                sx={{
                  color: isDark ? "#ffffff" : "#1A1A1A",
                  fontSize: { xs: "1.3rem", sm: "1.8rem" },
                }}
              >
                Reset Password
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: isDark ? "#aaaaaa" : "text.secondary",
                  fontSize: { xs: "0.8rem", sm: "0.9rem" },
                }}
              >
                {activeStep === 0 && "Enter your email to receive a reset code"}
                {activeStep === 1 &&
                  "Enter the 6-digit code sent to your email"}
                {activeStep === 2 && "Create your new password"}
              </Typography>
            </Box>

            <Stepper
              activeStep={activeStep}
              alternativeLabel={!isMobile}
              sx={{
                mb: { xs: 3, sm: 4 },
                "& .MuiStepLabel-root .Mui-active": { color: primaryColor },
                "& .MuiStepIcon-root.Mui-active": { color: primaryColor },
                "& .MuiStepIcon-root.Mui-completed": { color: primaryColor },
                "& .MuiStepLabel-label": {
                  fontSize: isMobile ? "0.65rem" : "0.8rem",
                },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Fade in>
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: isDark ? "rgba(244, 67, 54, 0.15)" : undefined,
                    color: isDark ? "#ef5350" : undefined,
                    border: `1px solid ${isDark ? "rgba(244, 67, 54, 0.2)" : "error.main"}`,
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}
            {success && (
              <Fade in>
                <Alert
                  severity="success"
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: isDark ? "rgba(76, 175, 80, 0.15)" : undefined,
                    color: isDark ? "#66bb6a" : undefined,
                    border: `1px solid ${isDark ? "rgba(76, 175, 80, 0.2)" : "success.main"}`,
                  }}
                  icon={<CheckCircle />}
                >
                  {success}
                  {activeStep === 2 && " Redirecting to login..."}
                </Alert>
              </Fade>
            )}

            {activeStep === 0 && (
              <form onSubmit={handleRequestOTP}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: isDark
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.02)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      },
                      "&:hover fieldset": { borderColor: primaryColor },
                      "&.Mui-focused fieldset": {
                        borderColor: primaryColor,
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: primaryColor,
                    },
                    "& .MuiInputBase-input": {
                      color: isDark ? "#ffffff" : undefined,
                      py: { xs: 1.5, sm: 2 },
                    },
                    "& .MuiInputLabel-root": {
                      color: isDark ? "#aaaaaa" : undefined,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email
                          sx={{ color: isDark ? "#666" : "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: { xs: 1.5, sm: 1.8 },
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: { xs: 15, sm: 16 },
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
                    boxShadow: `0 8px 24px ${primaryColor}4D`,
                    "&:hover": {
                      boxShadow: `0 12px 40px ${primaryColor}66`,
                      transform: "translateY(-2px)",
                    },
                    "&:active": { transform: "translateY(0)" },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Send Reset Code"
                  )}
                </Button>
              </form>
            )}

            {activeStep === 1 && (
              <form onSubmit={handleVerifyOTP}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="caption"
                    color={isDark ? "#aaaaaa" : "text.secondary"}
                    sx={{ display: "block", mb: 1 }}
                  >
                    We've sent a 6-digit code to <strong>{email}</strong>
                  </Typography>
                  <TextField
                    fullWidth
                    label="6-Digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.02)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          bgcolor: isDark
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.04)",
                        },
                        "&:hover fieldset": { borderColor: primaryColor },
                        "&.Mui-focused fieldset": {
                          borderColor: primaryColor,
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: primaryColor,
                      },
                      "& .MuiInputBase-input": {
                        color: isDark ? "#ffffff" : undefined,
                        letterSpacing: 8,
                        fontSize: { xs: "1.2rem", sm: "1.4rem" },
                        py: { xs: 1.5, sm: 2 },
                      },
                      "& .MuiInputLabel-root": {
                        color: isDark ? "#aaaaaa" : undefined,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VpnKey
                            sx={{ color: isDark ? "#666" : "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ maxLength: 6 }}
                  />
                </Box>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: { xs: 1.5, sm: 1.8 },
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: { xs: 15, sm: 16 },
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
                    boxShadow: `0 8px 24px ${primaryColor}4D`,
                    "&:hover": {
                      boxShadow: `0 12px 40px ${primaryColor}66`,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Verify OTP"
                  )}
                </Button>
              </form>
            )}

            {activeStep === 2 && (
              <form onSubmit={handleResetPassword}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: isDark
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.02)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      },
                      "&:hover fieldset": { borderColor: primaryColor },
                      "&.Mui-focused fieldset": {
                        borderColor: primaryColor,
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: primaryColor,
                    },
                    "& .MuiInputBase-input": {
                      color: isDark ? "#ffffff" : undefined,
                      py: { xs: 1.5, sm: 2 },
                    },
                    "& .MuiInputLabel-root": {
                      color: isDark ? "#aaaaaa" : undefined,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock
                          sx={{ color: isDark ? "#666" : "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: isDark
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.02)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      },
                      "&:hover fieldset": { borderColor: primaryColor },
                      "&.Mui-focused fieldset": {
                        borderColor: primaryColor,
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: primaryColor,
                    },
                    "& .MuiInputBase-input": {
                      color: isDark ? "#ffffff" : undefined,
                      py: { xs: 1.5, sm: 2 },
                    },
                    "& .MuiInputLabel-root": {
                      color: isDark ? "#aaaaaa" : undefined,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock
                          sx={{ color: isDark ? "#666" : "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: { xs: 1.5, sm: 1.8 },
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: { xs: 15, sm: 16 },
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
                    boxShadow: `0 8px 24px ${primaryColor}4D`,
                    "&:hover": {
                      boxShadow: `0 12px 40px ${primaryColor}66`,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </form>
            )}

            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Button
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: isDark ? "#aaaaaa" : undefined,
                  "&:hover": { color: primaryColor },
                }}
              >
                {activeStep === 0 ? "Back to Login" : "Back"}
              </Button>
            </Box>
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? "#666" : "text.disabled",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                }}
              >
                <Security sx={{ fontSize: 14 }} />
                Secured by V-Tech Security
              </Typography>
            </Box>
          </Paper>
        </Slide>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
